/*
 * TD - Product Range
 * Section: sections/td-product-range.liquid
 * Styles:  assets/section-td-product-range.css (DESKTOP_QUERY must match its 910px
 *          breakpoint)
 *
 * Disclosure semantics at every width - no role="tablist", nothing swapped on resize.
 * aria-expanded on each toggle is the open state; the `hidden` attribute on the matching
 * panel follows it (immediately, or once a mobile collapse animation finishes).
 *
 *   Desktop (>= 910px): exactly one panel open at all times. Clicking the open toggle is
 *     a no-op. Roving tabindex, arrow keys move focus and open in one step. No animation.
 *   Mobile  (<  910px): each row opens and closes independently, so any number can be
 *     open. Natural tab order (no roving tabindex). Escape closes the row that has focus.
 *     Rows expand and collapse with a height animation unless reduced motion is requested.
 */
if (!customElements.get('td-product-range')) {
  const DESKTOP_QUERY = '(min-width: 910px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  /* fill: forwards holds the end frame until onfinish settles `hidden`; without it the
     collapsed panel snaps back to full height for a frame before it is hidden. */
  const ANIMATION = { duration: 200, easing: 'ease-out', fill: 'forwards' };
  /* Start loading the hidden panels' images shortly before the section arrives, so
     opening a row never shows an empty tile while its (lazy) image downloads. */
  const PRELOAD_MARGIN = '200px 0px';

  const SELECTORS = {
    item: '[data-td-pr-item]',
    toggle: '[data-td-pr-toggle]',
    panel: '[data-td-pr-panel]',
    lazyImage: '[data-td-pr-panel] img[loading="lazy"]',
  };

  class TdProductRange extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll(SELECTORS.item));
      this.toggles = this.items.map((item) => item.querySelector(SELECTORS.toggle));
      this.panels = this.items.map((item) => item.querySelector(SELECTORS.panel));

      this.mq = window.matchMedia(DESKTOP_QUERY);
      this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
      this.animations = new Map();
      this.lastActiveIndex = Math.max(this.openIndex, 0);

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onNearViewport = this.onNearViewport.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.mq.addEventListener('change', this.onBreakpointChange);
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      if (!navigator.connection?.saveData) {
        this.preloadObserver = new IntersectionObserver(this.onNearViewport, {
          rootMargin: PRELOAD_MARGIN,
        });
        this.preloadObserver.observe(this);
      }

      this.syncTabindex();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.mq.removeEventListener('change', this.onBreakpointChange);
      this.preloadObserver?.disconnect();

      /* Settle rather than just cancel: a collapse cancelled mid-flight would otherwise
         never reach the onfinish that hides its panel. */
      this.panels.forEach((panel, i) => {
        this.stopAnimation(panel);
        panel.hidden = !this.isOpen(i);
      });
    }

    onNearViewport([entry]) {
      if (!entry.isIntersecting) return;

      this.querySelectorAll(SELECTORS.lazyImage).forEach((image) => {
        image.fetchPriority = 'low';
        image.loading = 'eager';
      });
      this.preloadObserver.disconnect();
    }

    isOpen(index) {
      return this.toggles[index].getAttribute('aria-expanded') === 'true';
    }

    /* First open row, or -1 when none is (only reachable below 910px). */
    get openIndex() {
      return this.toggles.findIndex((_, i) => this.isOpen(i));
    }

    /* Desktop: open one tab and close the rest, without animation. */
    open(index) {
      this.panels.forEach((panel, i) => {
        this.toggles[i].setAttribute('aria-expanded', i === index ? 'true' : 'false');
        this.stopAnimation(panel);
        panel.hidden = i !== index;
      });

      this.lastActiveIndex = index;
      this.syncTabindex();
    }

    /* Mobile: open or close one row, leaving the others as they are. */
    setRow(index, expand, { instant = false } = {}) {
      if (this.isOpen(index) === expand) return;

      const panel = this.panels[index];
      this.toggles[index].setAttribute('aria-expanded', expand ? 'true' : 'false');

      if (instant || this.reducedMotion.matches) {
        this.stopAnimation(panel);
        panel.hidden = !expand;
      } else {
        this.animatePanel(panel, expand);
      }

      if (expand) this.lastActiveIndex = index;
    }

    /*
     * Animates from the panel's current rendered size and opacity, so reversing a row
     * mid-animation picks up where it is. The panel is border-box, so its vertical padding
     * collapses along with the height.
     */
    animatePanel(panel, expand) {
      const current = getComputedStyle(panel);
      const fromHeight = panel.hidden ? 0 : panel.getBoundingClientRect().height;
      const fromPadding = panel.hidden ? ['0px', '0px'] : [current.paddingTop, current.paddingBottom];
      const fromOpacity = panel.hidden ? 0 : Number(current.opacity);

      this.stopAnimation(panel);
      panel.hidden = false;

      const settled = getComputedStyle(panel);
      const toHeight = expand ? panel.scrollHeight : 0;
      const toPadding = expand ? [settled.paddingTop, settled.paddingBottom] : ['0px', '0px'];

      panel.style.overflow = 'hidden';

      const animation = panel.animate(
        {
          height: [`${fromHeight}px`, `${toHeight}px`],
          paddingTop: [fromPadding[0], toPadding[0]],
          paddingBottom: [fromPadding[1], toPadding[1]],
          opacity: [fromOpacity, expand ? 1 : 0],
        },
        ANIMATION
      );

      this.animations.set(panel, animation);

      animation.onfinish = () => {
        if (!expand) panel.hidden = true;
        this.stopAnimation(panel);
      };
    }

    stopAnimation(panel) {
      const animation = this.animations.get(panel);
      if (!animation) return;

      animation.onfinish = null;
      animation.cancel();
      this.animations.delete(panel);
      panel.style.overflow = '';
    }

    /* Desktop keeps one toggle in the tab order; mobile gives every row its own stop. */
    syncTabindex() {
      const openIndex = this.openIndex;

      this.toggles.forEach((toggle, i) => {
        if (this.mq.matches) {
          toggle.tabIndex = i === openIndex ? 0 : -1;
        } else {
          toggle.removeAttribute('tabindex');
        }
      });
    }

    onClick(event) {
      const toggle = event.target.closest(SELECTORS.toggle);
      if (!toggle) return;

      const index = this.toggles.indexOf(toggle);

      if (this.mq.matches) {
        /* Desktop always keeps one panel open, so the open tab is a no-op there. */
        if (!this.isOpen(index)) this.open(index);
      } else {
        this.setRow(index, !this.isOpen(index));
      }
    }

    onKeydown(event) {
      if (!this.mq.matches) {
        if (event.key !== 'Escape') return;

        /* Close the row that has focus - its toggle or anything inside its panel. */
        const index = this.items.indexOf(event.target.closest(SELECTORS.item));
        if (index === -1 || !this.isOpen(index)) return;

        event.preventDefault();
        this.setRow(index, false);
        this.toggles[index].focus();
        return;
      }

      const toggle = event.target.closest(SELECTORS.toggle);
      if (!toggle) return;

      const index = this.toggles.indexOf(toggle);
      const last = this.toggles.length - 1;
      let next;

      switch (event.key) {
        case 'ArrowRight':
          next = index === last ? 0 : index + 1;
          break;
        case 'ArrowLeft':
          next = index === 0 ? last : index - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.open(next);
      this.toggles[next].focus();
    }

    onBreakpointChange() {
      if (!this.mq.matches) {
        this.syncTabindex();
        return;
      }

      /* Desktop shows one tab: keep the last row the visitor opened if it is still open,
         otherwise the first open row, otherwise restore the last active one. open()
         also settles any mid-flight mobile animation. */
      const keep = this.isOpen(this.lastActiveIndex) ? this.lastActiveIndex : this.openIndex;
      this.open(keep === -1 ? this.lastActiveIndex : keep);
    }

    /* The editor's block hooks sit on the toggle. Opens without animation so it doesn't
       compete with the editor scrolling the block into view. */
    onBlockSelect(event) {
      const index = this.toggles.indexOf(event.target.closest(SELECTORS.toggle));
      if (index === -1) return;

      if (this.mq.matches) {
        this.open(index);
      } else {
        this.setRow(index, true, { instant: true });
      }
    }
  }

  customElements.define('td-product-range', TdProductRange);
}
